import Cookies from 'cookies-js';
import { addYears, formatISO } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import type { Token } from '../@types/Token';
import type { RootState } from '../reducers';

interface CurrentUser {
  login: boolean;
  isNew: boolean;
  cid: string | null;
  unAuthId: string;
  userId: string | null;
  sessionId: string;
  userType: 'dealer' | 'private' | 'N/A';
  position: any;
  idp: string | null;
}

const COOKIE_NAME = 'wayke-tid';
const SESSION_NAME = 'wayke-sid';

const USER_DEALER = 'dealer';
const USER_PRIVATE = 'private';

let __current_user: CurrentUser | null = null;
const __current_token = null;
let __current_user_type: 'dealer' | 'private' | null = null;
let __current_sub: string | null = null;

const createTemporaryUserId = () => {
  const id = `unauth-${uuidv4()}`;
  Cookies.set(COOKIE_NAME, id, {
    expires: formatISO(addYears(new Date(), 99)),
    path: '/',
  });

  return id;
};

const createSessionId = () => {
  const id = uuidv4();
  Cookies.set(SESSION_NAME, id, {
    path: '/',
  });

  return id;
};

const getTemporaryUserId = () => Cookies.get(COOKIE_NAME) || createTemporaryUserId();

const getSessionId = () => Cookies.get(SESSION_NAME) || createSessionId();

const getUserId = () => getTemporaryUserId();

const getFromJwt = <T>(state: RootState, prop: keyof Token) => {
  if (!state || !state.auth || !state.auth.token) {
    return null;
  }

  try {
    return jwtDecode<Token>(state.auth.token)[prop] as T;
  } catch (_e) {
    return null;
  }
};

const getSubFromJwt = (state: RootState | null) => {
  if (!state || !state.auth || !state.auth.token) {
    return null;
  }

  if (__current_sub && __current_token === state.auth.token) {
    return __current_sub;
  }

  return (__current_sub = getFromJwt(state, 'sub'));
};

const getPrivateDealerFromJwt = (state: RootState | null): 'dealer' | 'private' | 'N/A' => {
  if (!state || !state.auth || !state.auth.token) {
    return 'N/A';
  }

  if (__current_user_type && __current_token === state.auth.token) {
    return __current_user_type;
  }

  const branches = getFromJwt<string | string[]>(state, 'belongs-to-branch');

  return (__current_user_type = branches && branches.length > 0 ? USER_DEALER : USER_PRIVATE);
};

const getIdp = (state: RootState | null) => (state ? getFromJwt<string>(state, 'idp') : null);

const getIsNewUser = (state: RootState | null) =>
  state ? getFromJwt<boolean>(state, 'is-new-user') || false : false;

const getLocation = (state: RootState | null) =>
  state && state.geo && state.geo.location && state.geo.location && state.geo.location.lat
    ? {
        city: state.geo.location.city,
        name: state.geo.location.name,
        location: {
          lat: state.geo.location.lat,
          lon: state.geo.location.lng,
        },
      }
    : null;

const getUser = (state: RootState | null, cid: string | null): CurrentUser =>
  (__current_user = {
    login: !!state?.auth?.token,
    isNew: getIsNewUser(state),
    cid,
    unAuthId: getUserId(),
    userId: getSubFromJwt(state),
    sessionId: getSessionId(),
    userType: getPrivateDealerFromJwt(state),
    position: getLocation(state),
    idp: getIdp(state),
  });

const getGtmTracker = () =>
  window && window.ga && window.ga.getAll ? window.ga.getAll()[0] : null;

const onClientObject = (fn: (v: string | null) => void) => {
  const tracker = getGtmTracker();
  if (tracker && tracker.get) {
    fn(tracker.get('clientId'));
  } else {
    fn(null);
  }
};

export const withUser = (state: RootState | null, fn: (v: any) => void) =>
  __current_user && __current_user.login
    ? fn(__current_user)
    : onClientObject((cid) => fn(getUser(state, cid)));

export const trackWithUser = (state: RootState, obj: any) => {
  if (state.liveshopping.reference) {
    obj.liveshoppingReference = state.liveshopping.reference;
  }

  const setDataOnObj = (o: any, userData: CurrentUser) => {
    o.userData = userData;
    return o;
  };

  if (__current_user && __current_user.login && __current_user.userType) {
    return setDataOnObj(obj, __current_user);
  }

  onClientObject((cid) => setDataOnObj(obj, getUser(state, cid)));

  return obj;
};
export default trackWithUser;
